import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  ProductsDataTable,
  getProductColumns,
  ProductStatusPill,
} from "../../../components/dashboard/products/products-table";
import { getProductsAsync } from "../../../slices/products";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  CheckBadgeIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import ProductModal from "../../../components/dashboard/products/ProductModal";
import { updateUrlWithException, dateToString } from "../../../components/Utils";
import { ProductActionEditBar } from "../../../components/dashboard/products/product-action-edit-bar";
import ProductMultiEditDialog from "../../../components/dashboard/products/product-multi-edit-dialog";
import ProductPriceChangeDialog from "../../../components/dashboard/products/product-price-change-dialog";
import { debounce } from "lodash";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { getProductCategoriesAsync } from "../../../slices/productCategory";
import { useSettings } from "../../../contexts/settings-context";
import { useNavigate } from "react-router-dom";
import { TableToolbar } from "../../../components/utils/TableToolbar";
import { useTableView } from "../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../components/utils/PMTableView/RightDrawer";
import { ProductWarehouseStatus } from "../../../components/dashboard/products/utils";
import { LabelDesignerDialog } from "../../../components/labels/LabelDesignerDialog";
import { loadProductRecords } from "../../../components/labels/labelRecords";

/** Build API filter array from shareable URL flags (products list). */
function parseProductListFiltersFromSearchParams(searchParams) {
  const filters = [];
  if (searchParams.get("productActive") !== "0") {
    filters.push({ name: "active", value: "true" });
  }
  if (searchParams.get("productWh") === "1") {
    filters.push({
      name: "warehouse_status_type",
      value: JSON.stringify([1, 2, 3]),
    });
  }
  if (searchParams.get("productUncat") === "1") {
    filters.push({ name: "uncategorized", value: "true" });
  }
  return filters;
}

function readInitialProductListController() {
  const sp =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  return {
    filters: parseProductListFiltersFromSearchParams(sp),
    page: sp.get("page") ? parseInt(sp.get("page"), 10) : 1,
    per_page: sp.get("per_page") ? parseInt(sp.get("per_page"), 10) : 50,
    query: sp.get("query") || "",
    sort: sp.get("sort") || "asc",
    sortBy: sp.get("sortBy") || "created_at",
    fields:
      "id,displayTitle,title,item_number,brand,status,warehouse_status_type_id,purchase_price,sales_price,model_year,main_picture.id,sku,short_description,active,isSparePart,warehouse,online_store,created_at",
  };
}

export const ProductsList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { priceFormatter } = useSettings();

  const productState = useSelector((store) => store.products);
  const categoryState = useSelector(
    (store) => store.productCategories || { data: [], isLoading: false }
  );

  const { isLoading } = productState;
  const [selectedItems, setSelectedItems] = useState([]);
  const [controller, setController] = useState(readInitialProductListController);
  const [drawerProduct, setDrawerProduct] = useState(null);

  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  // Build the API payload, preserving the legacy `show_inactive` behaviour.
  const buildPayload = (ctrl) => {
    const payload = { ...ctrl };
    if (!ctrl.filters.some((f) => f.name === "active")) {
      payload.show_inactive = true;
    }
    return payload;
  };

  // Column definitions + table-view state
  const columns = useMemo(
    () => getProductColumns(t, { priceFormatter }),
    [t, priceFormatter]
  );
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "products",
    columns: columnMeta,
    getSnapshot: () => ({
      filters: controller.filters,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      setController((c) => ({
        ...c,
        page: 1,
        filters: snap.filters || [],
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  // Sync URL (page, search, sort, product filter toggles).
  useEffect(() => {
    const hasWh = controller.filters.some(
      (f) => f.name === "warehouse_status_type"
    );
    const hasActive = controller.filters.some((f) => f.name === "active");
    const hasUncat = controller.filters.some((f) => f.name === "uncategorized");
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        productWh: hasWh ? "1" : "",
        productActive: hasActive ? "1" : "0",
        productUncat: hasUncat ? "1" : "",
      },
      "filters"
    );
  }, [controller]);

  // Handle ModuleFilter changes (for category and checkbox filters)
  const handleModuleFilterChange = (filters) => {
    const backendFilters = [];

    if (filters.categories && filters.categories.length > 0) {
      backendFilters.push({
        name: "category",
        value: JSON.stringify(filters.categories),
      });
    }
    if (filters.active === true) {
      backendFilters.push({ name: "active", value: "true" });
    }
    if (filters.uncategorized === true) {
      backendFilters.push({ name: "uncategorized", value: "true" });
    }
    if (filters.lagerforte === true) {
      backendFilters.push({
        name: "warehouse_status_type",
        value: JSON.stringify([1, 2, 3]),
      });
    }

    setController({
      ...controller,
      page: 1,
      query: filters.query ?? controller.query,
      filters: backendFilters,
    });
  };

  // Fetch categories for ModuleFilter with tree structure
  useEffect(() => {
    dispatch(
      getProductCategoriesAsync({ per_page: 1000, page: 1, parents: true })
    );
  }, [dispatch]);

  const categoryTree = categoryState.data || [];

  const getInitialModuleFilters = () => {
    const initialFilters = {
      query: controller.query || "",
      categories: [],
      active: controller.filters.some((f) => f.name === "active"),
      uncategorized: controller.filters.some((f) => f.name === "uncategorized"),
      lagerforte: controller.filters.some(
        (f) => f.name === "warehouse_status_type"
      ),
    };

    const categoryFilter = controller.filters.find((f) => f.name === "category");
    if (categoryFilter) {
      try {
        const categoryIds = JSON.parse(categoryFilter.value);
        initialFilters.categories = Array.isArray(categoryIds)
          ? categoryIds
          : [];
      } catch (e) {
        console.error("Error parsing category filter:", e);
      }
    }

    return initialFilters;
  };

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [multiEditDialogOpen, setMultiEditDialogOpen] = useState(false);
  const [priceChangeDialogOpen, setPriceChangeDialogOpen] = useState(false);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);

  const debouncedDispatchAction = useMemo(
    () =>
      debounce((ctrl) => {
        dispatch(getProductsAsync(buildPayload(ctrl)));
      }, 400),
    [dispatch]
  );

  useEffect(() => {
    return () => debouncedDispatchAction.cancel();
  }, [debouncedDispatchAction]);

  useEffect(() => {
    if (multiEditDialogOpen) return undefined;
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return undefined;
    }
    debouncedDispatchAction(controller);
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, multiEditDialogOpen, debouncedDispatchAction]);

  // Infinite scroll
  const handleLoadMore = () => {
    const nextPage = (controller.page || 1) + 1;
    skipNextDispatchRef.current = true;
    dispatch(
      getProductsAsync(buildPayload({ ...controller, page: nextPage, append: true }))
    );
    setController((c) => ({ ...c, page: nextPage }));
  };

  const headerMeta = [
    {
      label: productState.count + " Products",
      description: "",
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      label: productState.newestProductsCount + " New products",
      description: "",
      path: null,
      action: null,
      icon: ArrowUpIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      label: productState.synchedWithApiCount + " Synched with API",
      description: "",
      path: null,
      action: null,
      icon: ArrowUpIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
  ];

  const listCount =
    productState?.count != null ? `${productState.count}` : null;

  return (
    <>
      <DashboardHeader
        variant="list"
        subtitle=""
        actions={[]}
        title={t("Products")}
        listCount={listCount}
        meta={headerMeta}
        mainButton={{
          label: t("New"),
          action: () => setEditModalOpen(true),
          path: null,
          icon: PlusIcon,
        }}
      >
        <div className="p-5 space-y-4">
          <ModuleFilter
            filterCard
            disabled={isLoading}
            onFiltersChange={handleModuleFilterChange}
            initialFilters={getInitialModuleFilters()}
            searchPlaceholder={t("Search products...")}
            hideStatusFilter={true}
            categoryTree={categoryTree}
            customCheckboxButtonFilters={[
              { key: "active", label: t("Aktive"), defaultValue: true },
              { key: "lagerforte", label: t("Lagerførte"), defaultValue: false },
              {
                key: "uncategorized",
                label: t("Ikke kategoriserte"),
                defaultValue: false,
              },
            ]}
            actionBar={
              <ProductActionEditBar
                selectedItems={selectedItems}
                setMultiEditDialogOpen={() => setMultiEditDialogOpen(true)}
                setPriceChangeDialogOpen={() => setPriceChangeDialogOpen(true)}
                onPrintLabels={() => setLabelDialogOpen(true)}
              />
            }
            selectedItems={selectedItems}
          />

          {/* Table card */}
          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
            <TableToolbar
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <ProductsDataTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={productState}
              onItemsSelect={(items) => setSelectedItems(items)}
              density={tableView.density}
              hasMore={(productState?.count || 0) > (productState?.data?.length || 0)}
              onLoadMore={handleLoadMore}
              onRowClick={(product) => setDrawerProduct(product)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerProduct}
        onClose={() => setDrawerProduct(null)}
        eyebrow={drawerProduct ? drawerProduct.item_number || "" : ""}
        title={drawerProduct?.title || drawerProduct?.displayTitle || t("Products")}
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/products/${drawerProduct.id}`),
          },
        ]}
      >
        {drawerProduct && (
          <>
            <DrawerSection title={t("Details")}>
              <DrawerProps
                items={[
                  {
                    label: t("Status"),
                    value: <ProductStatusPill status={drawerProduct.status} />,
                  },
                  {
                    label: t("Lager status"),
                    value: <ProductWarehouseStatus report={drawerProduct} />,
                  },
                  {
                    label: t("Leverandør"),
                    value: drawerProduct.brand?.title || "—",
                  },
                  {
                    label: t("SKU"),
                    value: drawerProduct.sku || "—",
                  },
                  {
                    label: t("product.model_year"),
                    value:
                      drawerProduct.model_year != null &&
                      drawerProduct.model_year !== ""
                        ? drawerProduct.model_year
                        : "—",
                  },
                  {
                    label: t("Created"),
                    value: drawerProduct.created_at
                      ? dateToString(drawerProduct.created_at)
                      : "—",
                  },
                ]}
              />
            </DrawerSection>

            <DrawerSection title={t("Pricing")}>
              <DrawerProps
                items={[
                  {
                    label: t("Purchase Price"),
                    value: priceFormatter.format(
                      drawerProduct.purchase_price || 0
                    ),
                  },
                  {
                    label: t("Sales Price"),
                    value: priceFormatter.format(drawerProduct.sales_price || 0),
                  },
                ]}
              />
            </DrawerSection>

            <DrawerSection title={t("Properties")}>
              <div className="flex flex-wrap gap-2">
                {drawerProduct.warehouse && (
                  <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                    {t("Lagerført")}
                  </span>
                )}
                {drawerProduct.isSparePart && (
                  <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                    {t("Reservedel")}
                  </span>
                )}
                {drawerProduct.online_store && (
                  <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                    {t("Nettbutikk")}
                  </span>
                )}
                {drawerProduct.active ? (
                  <span className="inline-flex rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
                    {t("Active")}
                  </span>
                ) : (
                  <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                    {t("Inactive")}
                  </span>
                )}
              </div>
            </DrawerSection>

            {drawerProduct.short_description && (
              <DrawerSection title={t("Description")}>
                <p className="whitespace-pre-line text-[13.5px] leading-relaxed text-slate-600">
                  {drawerProduct.short_description}
                </p>
              </DrawerSection>
            )}
          </>
        )}
      </RightDrawer>

      <ProductModal open={editModalOpen} onClose={() => setEditModalOpen(false)} />

      <ProductMultiEditDialog
        selectedItems={selectedItems}
        open={multiEditDialogOpen}
        onClose={() => setMultiEditDialogOpen(false)}
      />

      <ProductPriceChangeDialog
        selectedItems={selectedItems}
        open={priceChangeDialogOpen}
        onClose={() => setPriceChangeDialogOpen(false)}
      />

      <LabelDesignerDialog
        module="product"
        open={labelDialogOpen && selectedItems.length > 0}
        onClose={() => setLabelDialogOpen(false)}
        loadRecords={() => loadProductRecords(selectedItems)}
      />
    </>
  );
};
